/**
 * SearchHub — one window that searches everything and holds all chats and all
 * notifications (docs/specs/2026-09-24-search-hub-design.md). Esc, the close
 * button, or opening a row closes it; clicking elsewhere leaves it open.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DurableConversation } from "@/api/conversations";
import {
  CONCEPT_ICONS,
  ModalSurfaceScope,
  PocketHeader,
  PocketPanel,
  TextInput,
} from "@/components/ui";
import {
  navigateGlobalSearchResult,
  type HubNavigationHandlers,
} from "@/components/layout/global-search-navigation";
import { useInteractiveLayer } from "@/components/layout/interactive-layer";
import { useDraggableModal } from "@/hooks/use-draggable-modal";
import { openNotificationItem } from "@/components/panels/notifications/notification-item";
import { useNotificationActions } from "@/components/panels/notifications/use-notification-actions";
import { openConversationChat } from "@/components/toasts/work-toast-actions";
import {
  HUB_CHIPS,
  type GlobalSearchRequest,
  type HubChip,
} from "@/lib/global-search";
import { useT } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { ChatRenameDialog } from "./chat-rename-dialog";
import { HubBody } from "./hub-body";
import { HubChipRow } from "./hub-chip-row";
import { GROUP_CHIPS } from "./hub-sections";
import type { HubGroupId, HubItem, HubResult } from "./hub-types";
import { openHubChat } from "./use-hub-chats";
import { useHubData } from "./use-hub-data";
import { useHubKeyboard } from "./use-hub-keyboard";

const HUB_PLACEHOLDER_KEYS = {
  all: "searchAll",
  chats: "searchChats",
  notifications: "searchNotifications",
  tasks: "searchTasks",
  frames: "searchFrames",
  memory: "searchMemory",
  settings: "searchSettings",
} as const satisfies Record<HubChip, string>;

export interface SearchHubProps {
  request: GlobalSearchRequest;
  onClose: () => void;
  navigation: HubNavigationHandlers;
}

export function SearchHub({ request, onClose, navigation }: SearchHubProps) {
  const t = useT(panelsSearchMessages);
  const [chip, setChip] = useState<HubChip>(request.chip);
  const [projectId, setProjectId] = useState<string | null>(request.projectId);
  const [query, setQuery] = useState(request.query ?? "");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<HubGroupId>>(
    () => new Set(),
  );
  const [renaming, setRenaming] = useState<DurableConversation | null>(null);
  const hubRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement>(null);
  const { position, dragHandleProps } = useDraggableModal({
    open: true,
    containerRef: hubRef,
  });
  const layer = useInteractiveLayer("search-hub", {
    active: true,
    baseZIndex: 70,
    tier: "modal",
  });
  const notificationActions = useNotificationActions();
  const { view, chats, notificationCount } = useHubData({
    chip,
    query,
    projectId,
    archivedExpanded,
    expandedGroups,
  });
  const items = useMemo(
    () => view.sections.flatMap((section) => section.items),
    [view],
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [chip, query, projectId]);

  useEffect(() => {
    const previous = document.activeElement;
    const hub = hubRef.current;
    const frame = requestAnimationFrame(() => fieldRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      const active = document.activeElement;
      if (
        previous instanceof HTMLElement &&
        previous.isConnected &&
        (active === document.body || active === null || hub?.contains(active))
      )
        previous.focus();
    };
  }, []);

  const openResult = useCallback(
    (result: HubResult) => {
      if (result.kind === "chat") {
        if (result.chat) {
          openHubChat(result.chat, onClose);
          return;
        }
        void openConversationChat(result.id, `hub-${result.id}`);
        onClose();
        return;
      }
      if (navigateGlobalSearchResult({ result, handlers: navigation }))
        onClose();
    },
    [navigation, onClose],
  );

  const activate = useCallback(
    (item: HubItem | undefined) => {
      if (!item) return;
      switch (item.type) {
        case "result":
          openResult(item.result);
          return;
        case "notification":
        case "approval":
          if (openNotificationItem(item, notificationActions)) onClose();
          return;
        case "archived":
          setArchivedExpanded((expanded) => !expanded);
          return;
        case "show-all": {
          const target = GROUP_CHIPS[item.group];
          if (target) setChip(target);
          else setExpandedGroups((groups) => new Set(groups).add(item.group));
          return;
        }
      }
    },
    [notificationActions, onClose, openResult],
  );

  useHubKeyboard({
    hubRef,
    fieldRef,
    query,
    onClearQuery: () => setQuery(""),
    onTypeCharacter: (character) => setQuery((text) => text + character),
    onStepChip: (step) =>
      setChip(
        (current) =>
          HUB_CHIPS[
            (HUB_CHIPS.indexOf(current) + step + HUB_CHIPS.length) %
              HUB_CHIPS.length
          ]!,
      ),
    onMoveSelection: (step) =>
      setSelectedIndex((index) =>
        items.length ? (index + step + items.length) % items.length : 0,
      ),
    onOpenSelected: () => activate(items[selectedIndex]),
    onClose,
  });

  // Portal to <body>: ancestors with backdrop-filter would otherwise become the
  // containing block for position:fixed.
  return createPortal(
    <ModalSurfaceScope>
      <PocketPanel
        ref={hubRef}
        nativeGroup="search-hub"
        testId="search-hub"
        role="dialog"
        aria-label={t("search")}
        data-interactive="true"
        className="fixed flex flex-col"
        style={{
          zIndex: layer.zIndex + 1,
          top: position ? position.y : "15vh",
          left: position ? position.x : "max(16px, calc(50vw - 430px))",
          width: "min(860px, calc(100vw - 32px))",
          maxHeight: "min(540px, calc(85vh - 24px))",
        }}
        {...layer.interactionProps}
      >
        <PocketHeader
          title={t("search")}
          onClose={onClose}
          testIdPrefix="search-hub"
          dragHandleProps={dragHandleProps}
        />
        <div className="relative flex-none">
          <CONCEPT_ICONS.search
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />
          <TextInput
            ref={fieldRef}
            shape="capsule"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t(HUB_PLACEHOLDER_KEYS[chip])}
            aria-label={t("search")}
            autoComplete="off"
            spellCheck={false}
            className="pl-10"
            data-testid="search-hub-input"
          />
        </div>
        <HubChipRow
          chip={chip}
          onChipChange={setChip}
          notificationCount={notificationCount}
          projectId={projectId}
          onProjectChange={setProjectId}
        />
        <HubBody
          view={view}
          query={query}
          selectedIndex={selectedIndex}
          onActivate={activate}
          onLookInAllProjects={() => setProjectId(null)}
          notificationActions={notificationActions}
          chats={chats}
          onRenameChat={setRenaming}
        />
      </PocketPanel>
      {renaming ? (
        <ChatRenameDialog
          chat={renaming}
          onRename={chats.rename}
          onClose={() => setRenaming(null)}
        />
      ) : null}
    </ModalSurfaceScope>,
    document.body,
  );
}
